import React from 'react';
import { Loader2, FileText } from 'lucide-react';
import { useFieldForms } from '../../hooks/useFieldForms';
import FormGroupCard from '../../components/field/FormGroupCard';
import MyDraftsList from '../../components/field/MyDraftsList';
import { useAuth } from '../../contexts/AuthContext';
import type { FormGroup } from '../../hooks/useFieldForms';

/**
 * The trip inspection is a built screen, not a `form_templates` row, so it is
 * not in `/field/forms` and is added here for a trucking tenant. This tab is
 * the only way into it from the drivers' app: the tree's bottom bar has no
 * Safety tab, so /field/safety (and its Equipment Inspection tile) is reachable
 * there by URL alone.
 */
const TRIP_GROUP: FormGroup = {
  group: 'Trip inspection',
  label: 'Trip inspection',
  vertical: 'trucking',
  forms: [
    { id: 'trip-inspection-pre', key: 'trip_inspection_pre', name: 'Pre-trip inspection', vertical: 'trucking', route: '/field/trip-inspections/new?type=pre_trip' },
    { id: 'trip-inspection-post', key: 'trip_inspection_post', name: 'Post-trip inspection', vertical: 'trucking', route: '/field/trip-inspections/new?type=post_trip' },
    { id: 'trip-inspection-mine', key: 'trip_inspection_mine', name: 'My trip inspections', vertical: 'trucking', route: '/field/trip-inspections' },
  ],
};

/**
 * The two safety forms this tree already registers and nothing linked to
 * (inspections PR 4): the generic pre-job meeting (job optional, free-text
 * location) and the JSA. `/field/safety/meeting`, the tile's redirect, is
 * casing-tree only — these are the forms themselves, at the routes THIS tree
 * registers.
 */
const SAFETY_GROUP: FormGroup = {
  group: 'Safety',
  label: 'Safety',
  forms: [
    { id: 'pre-job-meeting', key: 'pre_job_meeting', name: 'Pre-job meeting', vertical: 'safety', route: '/forms/pre-job-meeting' },
    { id: 'jsa', key: 'jsa', name: 'Job safety analysis (JSA)', vertical: 'safety', route: '/field/safety/jsa' },
  ],
};

export default function FormsPage() {
  const { data: apiGroups = [], isLoading } = useFieldForms();
  const { hasVertical } = useAuth();
  // Independent positive guards. This page renders only in the trucking /
  // rentals tree, but it says so itself rather than relying on that.
  const hauling = hasVertical('trucking') || hasVertical('surface_rentals');
  /*
   * ONE "SAFETY" GROUP (fix/field-home-trucking-rentals). GET /field/forms
   * returns its own "Safety" group, and on Apex Surface Rentals it carries the
   * same two forms as SAFETY_GROUP under other names: "Safety meeting sign-in"
   * opens /forms/pre-job-meeting (this group's "Pre-job meeting"), and
   * `system_jsa` is the JSA. The page drew two SAFETY headings with the JSA in
   * each. The built screens here are kept, the API's copies of them dropped,
   * and its remaining forms (near miss, toolbox talk, hazard ID) join them.
   */
  const apiSafety = apiGroups.find((g) => g.group.toLowerCase() === 'safety');
  const localRoutes = new Set(SAFETY_GROUP.forms.map((f) => f.route));
  const safety: FormGroup | null = hauling
    ? {
        ...SAFETY_GROUP,
        forms: [
          ...SAFETY_GROUP.forms,
          ...(apiSafety?.forms ?? []).filter((f) => !localRoutes.has(f.route) && f.key !== 'system_jsa'),
        ],
      }
    : null;
  const groups = [
    // A rentals tenant inspects the trucks that haul its equipment (#815, api#1043).
    ...(hauling ? [TRIP_GROUP] : []),
    ...(safety ? [safety] : []),
    ...apiGroups.filter((g) => !(safety && g === apiSafety)),
  ];
  const showHeaders = groups.length > 1;

  return (
    <div className="px-4 pt-4 pb-4">
      <div>
        {/* The page title is the shell's, beside its back arrow (fix/field-home-trucking-rentals). */}
        <p className="text-xs text-fieldiq-text">Self-initiated forms and inspections.</p>
      </div>

      <MyDraftsList />

      {isLoading && groups.length === 0 ? (
        <div className="flex justify-center py-12">
          <Loader2 size={28} className="animate-spin text-fieldiq-text" />
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-12">
          <FileText size={40} className="mx-auto text-fieldiq-text/40 mb-3" />
          <p className="text-sm text-fieldiq-text">No forms available</p>
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          {groups.map((group) => (
            <FormGroupCard key={group.group} group={group} showHeader={showHeaders} />
          ))}
        </div>
      )}
    </div>
  );
}
